import React, { useEffect, useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { usePhysicsSurvivalRunner } from '../../hooks/usePhysicsSurvivalRunner'
import { useGameAudio } from '../../hooks/useGameAudio'
import { useStudyStore } from '../../store/useStudyStore'
import { GameAnswer } from '../../types/database'
import { DoorQuestGameConfig } from '../../types'
import { SurvivalRunnerScene } from './SurvivalRunnerScene'
import { PlayCircle, RotateCcw, Home, Loader2, ChevronLeft, ChevronRight, Volume2, VolumeX, Trophy } from 'lucide-react'
import { RealTimeLeaderboard } from './RealTimeLeaderboard'
import './PhysicsSurvivalRunner.css'

interface PhysicsSurvivalRunnerProps extends DoorQuestGameConfig {
  onExit?: () => void
  onMusicControlsReady?: (controls: { toggle: () => void; isPlaying: boolean }) => void
}

/**
 * Component Game Physics Survival Runner
 * 
 * Nhân vật chạy liên tục trên 3 làn đường (trái - giữa - phải).
 * Chướng ngại vật xuất hiện với đáp án, người chơi phải di chuyển vào làn đúng.
 * Đúng: vượt qua, tăng tốc
 * Sai: game over
 */
export const PhysicsSurvivalRunner: React.FC<PhysicsSurvivalRunnerProps> = ({
  lop,
  chuong,
  bai,
  minQuestions = 6,
  maxQuestions = 10,
  onExit,
  onMusicControlsReady
}) => {
  const {
    gameState,
    isLoading,
    startGame,
    moveToLane,
    jump,
    resetGame,
    currentQuestion
  } = usePhysicsSurvivalRunner({
    lop,
    chuong,
    bai,
    minQuestions,
    maxQuestions
  })

  const controlOverlayRef = useRef<HTMLDivElement | null>(null)
  // Timer
  const [timeElapsed, setTimeElapsed] = useState(0)
  const [isTimerRunning, setIsTimerRunning] = useState(false)
  const [isPointerDown, setIsPointerDown] = useState(false)
  // Leaderboard
  const [showLeaderboard, setShowLeaderboard] = useState(false)

  // Study Store
  const { submitGame, startGame: startGameSession } = useStudyStore()

  // Track answers
  const [gameAnswers, setGameAnswers] = useState<GameAnswer[]>([])
  const [gameStartTime, setGameStartTime] = useState<number>(0)

  // Audio
  const [audioUnlocked, setAudioUnlocked] = useState(false)
  const bgMusic = useGameAudio({
    backgroundMusic: '/sounds/game-background.mp3',
    volume: 0.3,
    loop: true,
    autoPlay: false
  })

  const introMusic = useGameAudio({
    backgroundMusic: '/sounds/game-intro.mp3',
    volume: 0.4,
    loop: true,
    autoPlay: true
  })

  const victoryMusic = useGameAudio({
    backgroundMusic: '/sounds/game-victory.mp3',
    volume: 0.5,
    loop: false,
    autoPlay: false
  })

  // Timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1)
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isTimerRunning])

  useEffect(() => {
    if (gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
      setIsTimerRunning(true)
    } else {
      setIsTimerRunning(false)
    }
  }, [gameState.isGameActive, gameState.isGameWon, gameState.isGameLost])

  // Keyboard controls
  useEffect(() => {
    if (!gameState.isGameActive) return

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        moveToLane(Math.max(0, gameState.currentLane - 1))
      } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        moveToLane(Math.min(2, gameState.currentLane + 1))
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [gameState.isGameActive, gameState.currentLane, moveToLane])

  // Touch/Swipe controls
  useEffect(() => {
    if (!gameState.isGameActive) return

    let touchStartX = 0
    let touchStartY = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY

      // Chỉ xử lý swipe ngang (deltaX lớn hơn deltaY)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          // Swipe phải = di chuyển sang phải
          moveToLane(Math.min(2, gameState.currentLane + 1))
        } else {
          // Swipe trái = di chuyển sang trái
          moveToLane(Math.max(0, gameState.currentLane - 1))
        }
      }
    }

    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [gameState.isGameActive, gameState.currentLane, moveToLane])

  // Track answer khi có feedback (giống DoorQuestGame)
  useEffect(() => {
    if (gameState.showFeedback && currentQuestion && gameState.currentLane !== null) {
      // Tìm obstacle đang va chạm để lấy thông tin đáp án
      // Sử dụng collision range tương tự như trong hook
      const PLAYER_Z_POSITION = -0.5 // Phải khớp với giá trị trong usePhysicsSurvivalRunner.ts
      const collidingObstacle = gameState.obstacles.find(obs => 
        obs.position > PLAYER_Z_POSITION - 1.2 && 
        obs.position < PLAYER_Z_POSITION + 1.8 &&
        obs.lane === gameState.currentLane
      )
      
      if (collidingObstacle) {
        const isCorrect = gameState.currentLane === currentQuestion.correctIndex
        const answerTime = Math.floor((Date.now() - gameStartTime) / 1000) / Math.max(1, gameState.score + 1) // Ước tính thời gian trả lời
        
        const gameAnswer: GameAnswer = {
          questionId: currentQuestion.id,
          questionText: currentQuestion.question,
          userAnswer: currentQuestion.answers[gameState.currentLane],
          correctAnswer: currentQuestion.answers[currentQuestion.correctIndex],
          isCorrect,
          timeSpent: Math.max(1, Math.floor(answerTime)),
          roomNumber: gameState.score + 1 // Số câu đã trả lời đúng + 1
        }
        
        setGameAnswers(prev => {
          // Tránh duplicate - chỉ thêm nếu chưa có câu hỏi này
          if (prev.some(a => a.questionId === gameAnswer.questionId && a.roomNumber === gameAnswer.roomNumber)) {
            return prev
          }
          return [...prev, gameAnswer]
        })
      }
    }
  }, [gameState.showFeedback, gameState.currentLane, currentQuestion, gameState.obstacles, gameState.score, gameStartTime])

  // Save game result
  useEffect(() => {
    if (gameState.isGameWon && gameState.score > 0) {
      const saveGameResult = async () => {
        try {
          const gameId = `survival-runner-lop-${lop}-chuong-${chuong}-bai-${bai}`
          const lessonId = `${lop}-${chuong}-${bai}`
          const classId = `${lop}`
          const chapterId = `${chuong}`

          await submitGame(
            gameId,
            lessonId,
            classId,
            chapterId,
            gameState.score,
            gameState.totalQuestions,
            timeElapsed,
            gameAnswers,
            true,
            gameState.score
          )
        } catch (error) {
          console.error('Failed to save game result:', error)
        }
      }

      saveGameResult()
    }
  }, [gameState.isGameWon, gameState.score, gameState.totalQuestions, timeElapsed, lop, chuong, bai, submitGame, gameAnswers])

  // Handlers
  const handleStartGame = async () => {
    setTimeElapsed(0)
    setGameAnswers([])
    setGameStartTime(Date.now())
    
    // Dừng intro music ngay lập tức và đảm bảo không play lại
    introMusic.pause()
    
    const gameId = `survival-runner-lop-${lop}-chuong-${chuong}-bai-${bai}`
    const lessonId = `${lop}-${chuong}-${bai}`
    await startGameSession(gameId, lessonId)
    
    startGame()
    
    // Đảm bảo intro đã dừng hoàn toàn trước khi phát bg music
    // Gọi pause nhiều lần để đảm bảo
    introMusic.pause()
    setTimeout(() => {
      introMusic.pause() // Gọi lại để chắc chắn
      bgMusic.play()
    }, 100)
  }

  const handleResetGame = () => {
    setTimeElapsed(0)
    setGameAnswers([])
    setGameStartTime(0)
    resetGame()
  }

  const handleExit = () => {
    setTimeElapsed(0)
    setIsTimerRunning(false)
    if (onExit) onExit()
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleLaneSelection = useCallback((clientX: number) => {
    if (!controlOverlayRef.current) return
    const rect = controlOverlayRef.current.getBoundingClientRect()
    if (rect.width <= 0) return

    const relativeX = clientX - rect.left
    if (relativeX < 0 || relativeX > rect.width) return

    const laneWidth = rect.width / 3
    const targetLane = Math.min(2, Math.max(0, Math.floor(relativeX / laneWidth)))
    moveToLane(targetLane)
  }, [moveToLane])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!gameState.isGameActive) return
    setIsPointerDown(true)
    handleLaneSelection(event.clientX)
  }, [gameState.isGameActive, handleLaneSelection])

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isPointerDown || !gameState.isGameActive) return
    event.preventDefault()
    handleLaneSelection(event.clientX)
  }, [isPointerDown, gameState.isGameActive, handleLaneSelection])

  const handlePointerUp = useCallback(() => {
    setIsPointerDown(false)
  }, [])

  // Keyboard handler cho nhảy (Space)
  useEffect(() => {
    if (!gameState.isGameActive) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space' || event.key === ' ') {
        event.preventDefault()
        jump()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [gameState.isGameActive, jump])

  // Touch handler cho nhảy (double tap hoặc swipe up)
  useEffect(() => {
    if (!gameState.isGameActive) return

    let lastTapTime = 0
    let touchStartY = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndY = e.changedTouches[0].clientY
      const deltaY = touchStartY - touchEndY // Swipe up = positive

      // Double tap để nhảy (trong 300ms)
      const currentTime = Date.now()
      if (currentTime - lastTapTime < 300) {
        jump()
        lastTapTime = 0
      } else {
        lastTapTime = currentTime
      }

      // Hoặc swipe up để nhảy
      if (deltaY > 50) {
        jump()
      }
    }

    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [gameState.isGameActive, jump])

  // Unlock audio
  useEffect(() => {
    const unlockAudio = () => {
      setAudioUnlocked(true)
      if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
        setTimeout(() => introMusic.play(), 100)
      }
    }

    document.addEventListener('click', unlockAudio, { once: true })
    document.addEventListener('touchstart', unlockAudio, { once: true })

    return () => {
      document.removeEventListener('click', unlockAudio)
      document.removeEventListener('touchstart', unlockAudio)
    }
  }, [])

  // Music controls - sử dụng ref để tránh infinite loop
  const onMusicControlsReadyRef = useRef(onMusicControlsReady)
  useEffect(() => {
    onMusicControlsReadyRef.current = onMusicControlsReady
  }, [onMusicControlsReady])

  useEffect(() => {
    if (onMusicControlsReadyRef.current) {
      onMusicControlsReadyRef.current({
        toggle: () => {
          if (gameState.isGameActive) {
            bgMusic.toggle()
          } else if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
            introMusic.toggle()
          } else if (gameState.isGameWon) {
            victoryMusic.toggle()
          }
        },
        isPlaying: bgMusic.isPlaying || introMusic.isPlaying || victoryMusic.isPlaying
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgMusic.isPlaying, introMusic.isPlaying, victoryMusic.isPlaying, gameState.isGameActive, gameState.isGameWon, gameState.isGameLost])

  // Music state changes
  useEffect(() => {
    if (gameState.isGameWon) {
      victoryMusic.play()
      bgMusic.pause()
      introMusic.pause()
    } else if (gameState.isGameLost) {
      bgMusic.pause()
      introMusic.pause()
      victoryMusic.pause()
    } else if (gameState.isGameActive) {
      // Khi game đang chơi, dừng intro và phát bg music
      introMusic.pause()
      if (!bgMusic.isPlaying) {
        bgMusic.play()
      }
    }
  }, [gameState.isGameWon, gameState.isGameLost, gameState.isGameActive])

  useEffect(() => {
    // Chỉ play intro khi game không active, không win, không lose
    // Và đảm bảo intro không được play khi game đang active
    if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
      // Dừng tất cả nhạc khác trước
      bgMusic.pause()
      victoryMusic.pause()
      
      // Chỉ play intro sau khi đã dừng tất cả
      setTimeout(() => {
        // Kiểm tra lại điều kiện để tránh play khi game đã bắt đầu
        if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost && !introMusic.isPlaying) {
          introMusic.play()
        }
      }, 200)
    } else {
      // Nếu game đang active, đảm bảo intro đã dừng
      introMusic.pause()
    }
  }, [gameState.isGameActive, gameState.isGameWon, gameState.isGameLost, introMusic.isPlaying])

  useEffect(() => {
    return () => {
      bgMusic.pause()
      introMusic.pause()
      victoryMusic.pause()
    }
  }, [])

  // Intro screen
  if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full h-full min-h-full max-h-full overflow-y-auto bg-gradient-to-b from-blue-900 via-indigo-900 to-purple-900 text-white flex items-center justify-center p-4 sm:p-6"
      >
        {!audioUnlocked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500/90 text-black rounded-full font-bold z-50 flex items-center gap-2 px-6 py-3 text-sm"
          >
            <span className="text-xl">🔊</span>
            <span>Nhấp vào bất kỳ đâu để bật nhạc!</span>
          </motion.div>
        )}

        <div className="w-full max-w-2xl text-center space-y-6 py-6">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.8 }}
          >
            <h1 className="font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent text-4xl sm:text-5xl md:text-6xl mb-4">
              🏃 Physics Survival Runner 🏃
            </h1>
            <p className="text-lg sm:text-xl text-white mb-6">
              Chạy và trả lời đúng để sống sót!
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-6 space-y-4"
          >
            <h2 className="font-semibold text-yellow-300 text-xl">📜 Luật chơi:</h2>
            <ul className="text-left text-white space-y-3 text-sm sm:text-base">
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">🏃</span>
                <span>Nhân vật chạy liên tục trên 3 làn đường (trái - giữa - phải)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">🚧</span>
                <span>Chướng ngại vật xuất hiện với đáp án trên đầu</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">👆</span>
                <span>Vuốt trái/phải hoặc bấm nút ← → để di chuyển vào làn đúng</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">✅</span>
                <span>Đúng: vượt qua, tăng tốc, tăng điểm</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">❌</span>
                <span>Sai: đâm vào tường → Game Over!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">🦘</span>
                <span>Nhảy: Nhấn <kbd className="bg-white/20 px-2 py-1 rounded text-sm font-mono">SPACE</kbd> (Desktop) hoặc double tap/swipe lên (Mobile) để nhảy qua tường. Nhảy qua không tính điểm!</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-lg shrink-0">⚡</span>
                <span>Càng đúng nhiều, tốc độ càng tăng → Thử thách hơn!</span>
              </li>
            </ul>
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleStartGame}
            disabled={isLoading}
            className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white rounded-full font-bold shadow-2xl hover:shadow-orange-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto px-10 py-4 text-lg gap-3"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin w-6 h-6" />
                <span>Đang chuẩn bị...</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-6 h-6" />
                <span>START</span>
              </>
            )}
          </motion.button>

          {onExit && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={handleExit}
              className="text-white hover:text-gray-200 transition-colors flex items-center gap-2 mx-auto"
            >
              <Home size={20} />
              Quay lại
            </motion.button>
          )}
        </div>
      </motion.div>
    )
  }

  // Game screen
  if (gameState.isGameActive) {
    return (
      <div className="w-full h-screen overflow-hidden bg-gradient-to-b from-blue-900 to-purple-900 text-white relative">
        {/* Header - Không có nền, absolute để không chiếm không gian */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="absolute top-0 left-0 right-0 z-10 px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-1 sm:gap-3 flex-1 min-w-0">
            <span className="text-base sm:text-lg drop-shadow-lg">🏃</span>
            <p className="font-bold text-orange-400 text-xs sm:text-sm md:text-base truncate drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)' }}>
              <span className="hidden sm:inline">Điểm: </span>
              {gameState.score}/{gameState.totalQuestions}
            </p>
          </div>

          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <p className="font-bold text-yellow-400 text-xs sm:text-sm md:text-base drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)' }}>
              <span className="hidden sm:inline">Tốc độ: </span>
              {gameState.speed.toFixed(1)}x
            </p>
            <p className="font-mono text-xs sm:text-sm hidden sm:block drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)' }}>
              ⏱️ {formatTime(timeElapsed)}
            </p>
            <p className="font-mono text-xs sm:hidden drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)' }}>
              {formatTime(timeElapsed)}
            </p>
          </div>
        </motion.div>

        {/* Game Scene - Chiếm toàn bộ màn hình */}
        <div className="w-full h-full relative">
          <SurvivalRunnerScene
            currentLane={gameState.currentLane}
            obstacles={gameState.obstacles}
            currentQuestion={currentQuestion?.question || 'Chuẩn bị...'}
            speed={gameState.speed}
            isJumping={gameState.isJumping}
          />

          <div
            ref={controlOverlayRef}
            className="absolute inset-0 z-10 cursor-pointer"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onPointerLeave={handlePointerUp}
            style={{ touchAction: 'none' }}
          />

          {/* Leaderboard Toggle Button */}
          <button
            onClick={() => setShowLeaderboard(!showLeaderboard)}
            className="absolute top-16 right-4 z-30 bg-white/20 backdrop-blur-md rounded-full p-3 hover:bg-white/30 transition-colors"
            title="Xem bảng xếp hạng"
          >
            <Trophy className="w-5 h-5 text-yellow-400" />
          </button>

          {/* Leaderboard Overlay */}
          {showLeaderboard && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="absolute top-20 right-4 z-30 w-80 max-h-96 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl overflow-hidden"
            >
              <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white flex items-center justify-between">
                <h3 className="font-bold text-sm">🏆 Bảng xếp hạng</h3>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="text-white hover:text-gray-200 text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto p-2">
                <RealTimeLeaderboard
                  gameId={`survival-runner-lop-${lop}-chuong-${chuong}-bai-${bai}`}
                  classId={`${lop}`}
                  limit={5}
                  autoRefresh={true}
                  refreshInterval={5000}
                  showCurrentUser={true}
                />
              </div>
            </motion.div>
          )}

          {/* Control Buttons - Mobile */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-4 md:hidden z-20">
            <button
              onClick={() => moveToLane(Math.max(0, gameState.currentLane - 1))}
              disabled={gameState.currentLane === 0}
              className="bg-white/20 backdrop-blur-md rounded-full p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              onClick={() => moveToLane(Math.min(2, gameState.currentLane + 1))}
              disabled={gameState.currentLane === 2}
              className="bg-white/20 backdrop-blur-md rounded-full p-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={32} />
            </button>
          </div>

          {/* Feedback Overlay */}
          <AnimatePresence>
            {gameState.showFeedback && (
              <motion.div
                initial={{ scale: 0.5, opacity: 0, y: -30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.5, opacity: 0, y: -30 }}
                transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50"
              >
                <div
                  className={`px-6 py-3 rounded-xl text-center font-bold text-lg shadow-2xl ${
                    gameState.feedbackType === 'correct'
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}
                >
                  {gameState.feedbackMessage}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hint */}
          {!gameState.showFeedback && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-center text-white text-xs opacity-70 pointer-events-none px-2"
            >
              <p className="hidden sm:block">👆 Vuốt trái/phải hoặc dùng phím ← → để di chuyển</p>
              <p className="sm:hidden">👆 Vuốt trái/phải để di chuyển</p>
            </motion.div>
          )}
        </div>
      </div>
    )
  }

  // Victory screen
  if (gameState.isGameWon) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full h-screen overflow-y-auto bg-gradient-to-b from-yellow-100 via-orange-100 to-yellow-200 p-4"
      >
        <div className="max-w-6xl mx-auto space-y-6">
          <motion.div
            initial={{ scale: 0.8, y: -50 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', duration: 0.8 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-orange-600">
              🎉 CHIẾN THẮNG! 🎉
            </h1>
            <p className="text-2xl md:text-3xl text-orange-800">
              Bạn đã vượt qua {gameState.totalQuestions} chướng ngại vật!
            </p>

            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', damping: 10 }}
              className="bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 text-white rounded-3xl p-8 shadow-2xl border-4 border-white/40 inline-block"
            >
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl">⏱️</span>
                  <h3 className="text-3xl font-bold">Thời gian</h3>
                </div>
                <div className="font-mono font-black text-6xl tracking-wider">
                  {formatTime(timeElapsed)}
                </div>
              </div>
            </motion.div>

            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 inline-block mt-4">
              <p className="text-xl text-orange-700">
                Điểm số: <span className="text-3xl font-bold text-orange-600">{gameState.score}/{gameState.totalQuestions}</span>
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            {onExit && (
              <button
                onClick={handleExit}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-10 py-5 rounded-full text-xl font-bold shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 flex items-center gap-3"
              >
                <Home size={28} />
                Tiếp tục học
              </button>
            )}
            <button
              onClick={handleStartGame}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <PlayCircle size={24} />
              Chơi lại
            </button>
          </motion.div>
        </div>
      </motion.div>
    )
  }

  // Game Over screen
  if (gameState.isGameLost) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full h-screen overflow-hidden bg-gradient-to-b from-gray-900 via-red-900 to-black text-white flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="max-w-2xl w-full text-center space-y-8"
        >
          <h1 className="text-5xl md:text-7xl font-bold text-red-500">
            😢 Game Over!
          </h1>
          <p className="text-2xl md:text-3xl text-gray-300">
            Bạn đã đâm vào tường, về nhà ôn bài kỹ hơn nhé, chúc bạn may mắn lần sau!
          </p>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <p className="text-xl">
              Đã vượt qua: <span className="text-2xl font-bold text-yellow-400">{gameState.score}/{gameState.totalQuestions}</span> chướng ngại vật
            </p>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={handleStartGame}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <RotateCcw size={24} />
              Thử lại
            </button>
            {onExit && (
              <button
                onClick={handleExit}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                <Home size={24} />
                Thoát
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    )
  }

  return null
}

